import Phaser from 'phaser';
import { setMissingTexturePaint } from './bindTexture';

const CREAM = 0xf3e2c0;
const PALETTE = [0xc4783a, 0x6b8f71, 0xd0892a, 0x8a4b6a, 0x3d6f8a, 0xc45a3a];

export function standInKey(key: string): string {
  return `standin-${key}`;
}

function tone(key: string): number {
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) hash = (hash * 33 + key.charCodeAt(i)) >>> 0;
  return PALETTE[hash % PALETTE.length] ?? PALETTE[0];
}

function standSize(key: string): number {
  if (key.includes('hero-')) return 512;
  if (key.includes('chest')) return 256;
  return 128;
}

/**
 * A warm stand-in registered under `key`.
 * The real file key stays empty so a late load can replace the stand-in.
 */
export function paintStandIn(scene: Phaser.Scene, key: string): string {
  if (scene.textures.exists(key)) return key;
  const size = standSize(key);
  const g = scene.make.graphics({ x: 0, y: 0 }, false);
  const color = tone(key.replace(/^standin-/, ''));
  const mid = size / 2;
  if (key.includes('chest')) {
    g.fillStyle(0x6b3e22, 1);
    g.fillRoundedRect(size * 0.12, size * 0.28, size * 0.76, size * 0.5, size * 0.08);
    g.fillStyle(0xc4844a, 1);
    g.fillRoundedRect(size * 0.1, size * 0.2, size * 0.8, size * 0.16, size * 0.05);
    g.fillStyle(0xf0c14a, 1);
    g.fillCircle(mid, size * 0.52, size * 0.06);
  } else if (key.includes('portrait-') || key.includes('hero-')) {
    g.fillStyle(color, 1);
    g.fillCircle(mid, mid, size * 0.42);
    g.fillStyle(CREAM, 1);
    g.fillCircle(mid, size * 0.46, size * 0.16);
    g.fillStyle(0x3b2414, 1);
    g.fillCircle(size * 0.38, size * 0.42, size * 0.045);
    g.fillCircle(size * 0.62, size * 0.42, size * 0.045);
  } else if (key.includes('cosmetic-')) {
    g.fillStyle(0xe7c98a, 1);
    g.fillRoundedRect(size * 0.14, size * 0.14, size * 0.72, size * 0.72, size * 0.14);
    g.fillStyle(color, 1);
    g.fillCircle(mid, size * 0.42, size * 0.16);
    g.fillStyle(0xf0c14a, 1);
    g.fillRoundedRect(size * 0.36, size * 0.58, size * 0.28, size * 0.14, size * 0.06);
  } else {
    g.fillStyle(CREAM, 1);
    g.fillRoundedRect(size * 0.1, size * 0.1, size * 0.8, size * 0.8, size * 0.16);
    g.fillStyle(color, 1);
    g.fillCircle(mid, mid, size * 0.2);
  }
  g.generateTexture(key, size, size);
  g.destroy();
  return key;
}

/** Images ask for this when their texture key is not registered yet. */
export function installTextureStandIn(): void {
  setMissingTexturePaint((scene, key) => {
    const host = scene as Phaser.Scene;
    if (!host.make || !host.textures) return null;
    const stand = standInKey(key);
    paintStandIn(host, stand);
    return host.textures.exists(stand) ? stand : null;
  });
}
